/**
 * Where a signed-in user lands (JSS#1155): `/` and a sign-in with no page to
 * return to send each role to the surface it works in.
 *
 *   superuser        -> /home
 *   site-operator    -> /sites
 *   event-operator   -> /audience/events
 *   everyone else    -> /home   (admin, engineer, a member with no role)
 *
 * The role is the one in the acting org when the BFF has answered
 * (`orgRole`, as `displayRole` uses it), else the highest realm persona.
 *
 * A role's surface the acting org doesn't have (#1160) lands on /home
 * instead. No entitlements (the read failed) counts as on, as everywhere:
 * the BFF enforces the real answer.
 */
import type { SurfaceEntitlements } from '@jeket-cloud/types';
import { highestRole, isRole, Role } from '@jeket-cloud/types/auth';
import { SURFACES, surfaceEntitled, surfaceForPath } from '@/surfaces';

export const HOME_PATH = '/home';

const LANDING_BY_ROLE: Partial<Record<Role, string>> = {
  [Role.SiteOperator]: '/sites',
  [Role.EventOperator]: '/audience/events',
};

export interface LandingUser {
  role?: string;
  roles?: readonly string[];
  orgRole?: string | null;
}

export function landingPath(
  user: LandingUser | null | undefined,
  entitlements?: Partial<SurfaceEntitlements> | null,
): string {
  if (!user || user.role === 'superuser') return HOME_PATH;
  const role = isRole(user.orgRole) ? user.orgRole : highestRole(user.roles ?? []);
  const path = role && LANDING_BY_ROLE[role];
  if (!path) return HOME_PATH;
  const surface = surfaceForPath(SURFACES, path);
  return !surface || surfaceEntitled(surface, user, entitlements) ? path : HOME_PATH;
}

/**
 * Where /auth/callback goes: the `returnTo` the sign-in started from, when it
 * names a page; otherwise `/`, which lands by role. `/`, `/login` and the
 * callback itself are not pages to return to (Login's sign-in button starts
 * from `/login`).
 *
 * Only a same-origin path is honoured: `returnTo` is resolved against this
 * origin, and anything that lands elsewhere (`//evil.com`, `/\evil.com`,
 * `https://…`, `javascript:…`) or whose path does not start with a single
 * `/` goes to `/` instead.
 */
export function callbackDestination(returnTo: unknown, origin: string = window.location.origin): string {
  if (typeof returnTo !== 'string' || returnTo === '') return '/';
  let url: URL;
  try {
    url = new URL(returnTo, origin);
  } catch {
    return '/';
  }
  if (url.origin !== origin) return '/';
  const { pathname, search, hash } = url;
  if (!pathname.startsWith('/') || pathname.startsWith('//') || pathname.startsWith('/\\')) return '/';
  if (pathname === '/' || pathname === '/login' || pathname === '/auth/callback') return '/';
  return pathname + search + hash;
}
